import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, UserRoundCog } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { FEATURE_KEYS, FEATURE_LABEL, platformChangePlanInput, platformExtendTrialInput, platformScheduleDeletionInput, platformSuspendOrgInput, type FeatureKey, type PlatformOrgDetail } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { api, errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { Pill } from '@/features/finance/pill';
import { ImpersonateModal } from './impersonate-modal';
import { OrgMembersCard } from './org-members-card';
import { usePlans } from './orgs-page';
import { Card, dd, dt, INVOICE_LABEL, INTERVAL_LABEL, microsMoney, OrgPill, SubPill, UsageBars, usePlatformAbility } from './shared';

const WA_LABEL = { connected: 'Conectado', connecting: 'Conectando', disconnected: 'Desconectado' } as const;

type Modal = null | 'suspend' | 'plan' | 'trial' | 'delete' | 'impersonate';

function useAct(id: string, done?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ method, path, body }: { method: 'post' | 'put'; path: string; body?: unknown }) => (method === 'put' ? api.put(path, body) : api.post(path, body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success('Ação aplicada');
      done?.();
    },
    onError: (e) => toast.error(errorMessage(e)),
    meta: { id },
  });
}

function SuspendModal({ id, onClose }: { id: string; onClose: () => void }) {
  const act = useAct(id, onClose);
  const form = useForm<z.input<typeof platformSuspendOrgInput>>({ resolver: zodResolver(platformSuspendOrgInput) });
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Suspender organização"
      description="A barbearia continua com leitura e exportação. Nenhum dado é apagado."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="suspend-form" variant="destructive" disabled={act.isPending}>Suspender</Button>
        </>
      }
    >
      <form id="suspend-form" noValidate onSubmit={form.handleSubmit((v) => act.mutate({ method: 'post', path: `/platform/orgs/${id}/suspend`, body: v }))}>
        <Field id="sus-reason" label="Motivo" error={form.formState.errors.reason?.message}>
          <Textarea id="sus-reason" rows={3} {...form.register('reason')} />
        </Field>
      </form>
    </FormModal>
  );
}

function PlanModal({ id, currentPlanId, onClose }: { id: string; currentPlanId?: string; onClose: () => void }) {
  const act = useAct(id, onClose);
  const plans = usePlans();
  const form = useForm<z.input<typeof platformChangePlanInput>>({ resolver: zodResolver(platformChangePlanInput), defaultValues: { planId: currentPlanId } });
  const planId = form.watch('planId');
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Trocar plano"
      description="A troca vale na hora. Rebaixar é bloqueado se o uso passa dos limites do novo plano."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="plan-form" disabled={act.isPending}>Trocar plano</Button>
        </>
      }
    >
      <form id="plan-form" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => act.mutate({ method: 'post', path: `/platform/subscriptions/${id}/change-plan`, body: v }))}>
        <Field id="pl-plan" label="Plano" error={form.formState.errors.planId?.message}>
          <Select value={planId} onValueChange={(v) => form.setValue('planId', v, { shouldValidate: true })}>
            <SelectTrigger id="pl-plan" className="w-full"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {(plans.data ?? []).filter((p) => p.active).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.priceCents)}/{INTERVAL_LABEL[p.interval]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="pl-note" label="Observação (opcional)">
          <Input id="pl-note" {...form.register('note')} />
        </Field>
      </form>
    </FormModal>
  );
}

function TrialModal({ id, onClose }: { id: string; onClose: () => void }) {
  const act = useAct(id, onClose);
  const form = useForm<z.input<typeof platformExtendTrialInput>>({ resolver: zodResolver(platformExtendTrialInput), defaultValues: { days: 7 } });
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Estender período de teste"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="trial-form" disabled={act.isPending}>Estender</Button>
        </>
      }
    >
      <form id="trial-form" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => act.mutate({ method: 'post', path: `/platform/subscriptions/${id}/extend-trial`, body: v }))}>
        <Field id="tr-days" label="Dias a acrescentar" error={form.formState.errors.days?.message}>
          <Input id="tr-days" type="number" min={1} {...form.register('days', { valueAsNumber: true })} />
        </Field>
        <Field id="tr-note" label="Observação (opcional)">
          <Input id="tr-note" {...form.register('note')} />
        </Field>
      </form>
    </FormModal>
  );
}

function DeleteModal({ id, name, onClose }: { id: string; name: string; onClose: () => void }) {
  const act = useAct(id, onClose);
  const form = useForm<z.input<typeof platformScheduleDeletionInput>>({ resolver: zodResolver(platformScheduleDeletionInput), defaultValues: { force: false } });
  const typed = form.watch('confirmName');
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Agendar exclusão"
      description="Etapa 1 de 2: a organização fica marcada para exclusão e só é apagada depois da janela de segurança. Dá para cancelar até lá."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="del-form" variant="destructive" disabled={act.isPending || typed !== name}>Agendar exclusão</Button>
        </>
      }
    >
      <form id="del-form" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => act.mutate({ method: 'post', path: `/platform/orgs/${id}/schedule-deletion`, body: v }))}>
        <Field id="del-name" label={`Digite "${name}" para confirmar`} error={form.formState.errors.confirmName?.message}>
          <Input id="del-name" autoComplete="off" {...form.register('confirmName')} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={form.watch('force')} onCheckedChange={(c) => form.setValue('force', c)} aria-label="Ignorar prazo de retenção" />
          Ignorar o prazo de retenção (uso excepcional)
        </label>
      </form>
    </FormModal>
  );
}

export function OrgDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { isOwner, canModerate, canBilling } = usePlatformAbility();
  const [modal, setModal] = useState<Modal>(null);
  const q = useQuery({ queryKey: ['platform', 'org', id], queryFn: () => api.get<PlatformOrgDetail>(`/platform/orgs/${id}`) });
  const act = useAct(id);
  const [busyExport, setBusyExport] = useState(false);
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const o = d.organization;
  const sub = d.subscription;
  const overrideSet = (key: FeatureKey, value: boolean | null) =>
    api
      .put(`/platform/orgs/${id}/feature-overrides`, { overrides: { [key]: value } })
      .then(() => qc.invalidateQueries({ queryKey: ['platform', 'org', id] }))
      .catch((e) => toast.error(errorMessage(e)));

  return (
    <div className="grid grid-cols-1 gap-6">
      <div>
        <Link to="/plataforma/organizacoes" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Organizações
        </Link>
        <PageHeader
          title={o.name}
          description={`${o.slug} · cadastrada em ${dd(o.createdAt)}${d.owner ? ` · proprietário ${d.owner.name} (${d.owner.email})` : ''}`}
          actions={
            <>
              <Button
                variant="outline"
                disabled={busyExport}
                onClick={async () => {
                  setBusyExport(true);
                  try {
                    await api.download(`/platform/orgs/${id}/export`, undefined, `${o.slug}.zip`);
                  } catch (e) {
                    toast.error(errorMessage(e));
                  } finally {
                    setBusyExport(false);
                  }
                }}
              >
                <Download className="size-4" /> Exportar dados
              </Button>
              {canModerate && (
                <Button variant="outline" onClick={() => setModal('impersonate')}>
                  <UserRoundCog className="size-4" /> Entrar como
                </Button>
              )}
              {canModerate && o.status === 'active' && <Button variant="destructive" onClick={() => setModal('suspend')}>Suspender</Button>}
              {canModerate && o.status !== 'active' && (
                <ConfirmDialog
                  destructive={false}
                  trigger={<Button>Reativar</Button>}
                  title="Reativar organização?"
                  description="A barbearia volta a poder gravar dados imediatamente."
                  confirmLabel="Reativar"
                  onConfirm={() => act.mutateAsync({ method: 'post', path: `/platform/orgs/${id}/reactivate`, body: {} })}
                />
              )}
            </>
          }
        />
      </div>

      {o.status !== 'active' && (
        <div role="status" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <strong>{o.status === 'suspended' ? 'Organização suspensa' : 'Exclusão agendada'}</strong>
          {o.suspendedReason && <> — {o.suspendedReason}</>}
          {o.suspendedAt && <> (desde {dt(o.suspendedAt)})</>}
          {o.deletionScheduledFor && <>. Será apagada em {dt(o.deletionScheduledFor)}.</>}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Assinatura"
          actions={
            sub && (
              <>
                {canBilling && <Button size="sm" variant="outline" onClick={() => setModal('plan')}>Trocar plano</Button>}
                {canModerate && sub.status === 'trialing' && <Button size="sm" variant="outline" onClick={() => setModal('trial')}>Estender teste</Button>}
              </>
            )
          }
        >
          {sub ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">Situação</dt><dd className="flex gap-2"><OrgPill status={o.status} /><SubPill status={sub.status} /></dd>
              <dt className="text-muted-foreground">Plano</dt><dd>{sub.planName} · {money(sub.priceCents)}/{INTERVAL_LABEL[sub.interval]}</dd>
              <dt className="text-muted-foreground">Teste até</dt><dd>{dd(sub.trialEndsAt)}</dd>
              <dt className="text-muted-foreground">Período atual</dt><dd>{dd(sub.currentPeriodStart)} a {dd(sub.currentPeriodEnd)}</dd>
              <dt className="text-muted-foreground">Carência até</dt><dd>{dd(sub.graceUntil)}</dd>
              <dt className="text-muted-foreground">Cancelamento</dt><dd>{sub.cancelAtPeriodEnd ? 'Agendado para o fim do período' : sub.canceledAt ? dt(sub.canceledAt) : 'Não'}</dd>
              <dt className="text-muted-foreground">Custo de IA</dt><dd>{microsMoney(d.aiCostMicros)}</dd>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Sem assinatura (organização anterior à cobrança): usa todos os recursos sem limite.</p>
          )}
        </Card>
        <Card title="WhatsApp" description="Somente leitura. O número conectado não é exibido.">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">Situação</dt>
            <dd>{d.whatsapp ? WA_LABEL[d.whatsapp.status] : 'Nunca conectado'}</dd>
            <dt className="text-muted-foreground">Conectado desde</dt>
            <dd>{dt(d.whatsapp?.connectedAt)}</dd>
          </dl>
        </Card>
        <Card title="Uso e limites">
          <UsageBars usage={d.usage} />
        </Card>
        <Card title="Recursos" description="Sobrescrita por organização: 'Plano' segue o plano contratado.">
          <ul className="divide-y">
            {FEATURE_KEYS.map((k) => {
              const v = d.featureOverrides[k];
              return (
                <li key={k} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span>{FEATURE_LABEL[k]}</span>
                  <Select value={v === undefined ? 'plan' : v ? 'on' : 'off'} onValueChange={(x) => overrideSet(k, x === 'plan' ? null : x === 'on')} disabled={!canBilling}>
                    <SelectTrigger size="sm" className="w-36" aria-label={`Recurso ${FEATURE_LABEL[k]}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plan">Plano</SelectItem>
                      <SelectItem value="on">Forçar ligado</SelectItem>
                      <SelectItem value="off">Forçar desligado</SelectItem>
                    </SelectContent>
                  </Select>
                </li>
              );
            })}
          </ul>
        </Card>
        <Card title="Histórico da assinatura">
          {d.history.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem eventos.</p>
          ) : (
            <ul className="divide-y text-sm">
              {d.history.map((h) => (
                <li key={h.id} className="py-2">
                  <div className="flex flex-wrap justify-between gap-x-3">
                    <span className="font-medium">{ACTION[h.action] ?? h.action}</span>
                    <span className="tabular text-muted-foreground">{dt(h.at)}</span>
                  </div>
                  <p className="text-muted-foreground">
                    {h.fromPlanName ?? '—'} → {h.toPlanName ?? '—'}{h.actor ? ` · ${h.actor}` : ''}{h.note ? ` · ${h.note}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <OrgMembersCard orgId={id} orgName={o.name} />

      <Card title="Faturas">
        {d.invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma fatura.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-medium">Vencimento</th><th className="py-1 font-medium">Situação</th><th className="py-1 text-right font-medium">Valor</th><th className="py-1 font-medium">Pago em</th></tr></thead>
              <tbody>
                {d.invoices.map((i) => (
                  <tr key={i.id} className="border-t">
                    <td className="py-1.5">{dd(i.dueDate)}</td>
                    <td className="py-1.5"><Pill tone={i.status === 'paid' ? 'good' : i.status === 'overdue' ? 'bad' : 'neutral'}>{INVOICE_LABEL[i.status]}</Pill></td>
                    <td className="tabular py-1.5 text-right">{money(i.amountCents)}</td>
                    <td className="py-1.5">{dd(i.paidAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {isOwner && (
        <Card title="Exclusão" description="Processo em duas etapas. Suspender nunca apaga dados; excluir apaga depois da janela de segurança.">
          {o.status === 'pending_deletion' ? (
            <ConfirmDialog
              destructive={false}
              trigger={<Button variant="outline">Cancelar exclusão agendada</Button>}
              title="Cancelar a exclusão?"
              description="A organização volta ao estado suspenso e os dados são mantidos."
              confirmLabel="Cancelar exclusão"
              onConfirm={() => act.mutateAsync({ method: 'post', path: `/platform/orgs/${id}/cancel-deletion`, body: {} })}
            />
          ) : (
            <Button variant="destructive" onClick={() => setModal('delete')}>Agendar exclusão…</Button>
          )}
        </Card>
      )}

      {modal === 'impersonate' && <ImpersonateModal orgId={id} orgName={o.name} onClose={() => setModal(null)} />}
      {modal === 'suspend' && <SuspendModal id={id} onClose={() => setModal(null)} />}
      {modal === 'plan' && <PlanModal id={id} currentPlanId={sub?.planId} onClose={() => setModal(null)} />}
      {modal === 'trial' && <TrialModal id={id} onClose={() => setModal(null)} />}
      {modal === 'delete' && <DeleteModal id={id} name={o.name} onClose={() => setModal(null)} />}
    </div>
  );
}

const ACTION: Record<string, string> = {
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
  cancel: 'Cancelamento',
  reactivate: 'Reativação',
  trial_start: 'Início do teste',
  trial_convert: 'Teste convertido',
  extend_trial: 'Teste estendido',
  suspend: 'Suspensão',
};
export { ACTION as SUBSCRIPTION_ACTION_LABEL };
